import * as React from "react";
import {useTheme} from "@mui/material/styles";
import MDBox from "components/MDBox";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  AppBar,
  Button,
  Card,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  LastPage,
  FirstPage,
  NavigateBefore,
  NavigateNext
} from "@mui/icons-material";
import {GET} from "APIclient";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import * as XLSX from "xlsx";
import {useEffect, useState} from "react";
import dayjs from "dayjs";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import {formatDesignation} from "constants/ApplicationConstants";
import {toBackendDesignationFormat} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";
import {formatDate} from "constants/ApplicationConstants";
import copyableCell from "components/CopyableCell"

const rowLength = 20;


function CustomPagination({ 
  page, 
  pageSize, 
  totalRows, 
  totalPages, 
  onPageChange, 
  onGoToLastPage,
  loading 
}) {
  const handleFirstPage = () => {
    onPageChange(0);
  };

  const handlePreviousPage = () => {
    onPageChange(Math.max(0, page - 1));
  };

  const handleNextPage = () => {
    onPageChange(Math.min(totalPages - 1, page + 1));
  };

  const handleLastPage = () => {
    onGoToLastPage();
  };

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        borderTop: '1px solid #e0e0e0'
      }}
    >
      <Typography variant="caption" sx={{ color: '#666' }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : 'No rows'}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: '#666' }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>
        
        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: properties?.colors?.bgcolorbluethem || '#1565c0',
              },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function JoiningReport() {
  const theme = useTheme();
  const [empId, setEmpId] = useState("");
  const [empName, setEmpName] = useState("");
  const [designation, setDesignation] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setSize] = useState(rowLength);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [designationList, setDesignationList] = useState([]);

  const columns = [
    {
      field: "id",
      headerName: "#",
      width: 30,
      align: "left",
      headerAlign: "left"
    },
    {field: "empId", headerName: "EMP ID", width: 70, align: "left", headerAlign: "left"},
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 160,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "mobileNo",
      headerName: "Mobile No.",
      width: 120,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "officialEmailAddress",
      headerName: "Official ID",
      width: 250,
      renderCell:copyableCell
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 200,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "joinDate",
      headerName: "Joining Date",
      width: 150,
      align: "left",
      headerAlign: "left",
      renderCell: params => {
        const formattedDate = formatDate(params.value);
        return formattedDate || " - ";
      }
    }
  ];

  const fetchData = async (
    page,
    pageSize,
    empId,
    empName,
    designation,
    startDate,
    endDate,
    signal
  ) => {
    try {
      setLoading(true);
      const startDateFormatted = startDate ? startDate.format("YYYY-MM-DD") : "";
      const endDateFormatted = endDate ? endDate.format("YYYY-MM-DD") : "";
      const backendDesignation = toBackendDesignationFormat(designation);
      const queryParams = [
        `page=${page}`,
        `size=${pageSize}`,
        empId.trim() ? `empId=${empId}` : "",
        empName.trim() ? `empName=${encodeURIComponent(empName)}` : "",
        backendDesignation ? `designation=${encodeURIComponent(backendDesignation)}` : "",
        startDateFormatted ? `startDate=${startDateFormatted}` : "",
        endDateFormatted ? `endDate=${endDateFormatted}` : ""
      ]
        .filter(param => param)
        .join("&");
      const url = `admin/joining-report?${queryParams}`;
      console.log("API URL:", url);

      const response = await GET(url, {signal});

      if (response?.status === 200) {
        const formatted = (response?.data?.data?.paginatedData || []).map((item, index) => ({
          ...item,
          id: page * pageSize + index + 1,
          designation: formatDesignation(item?.designation)
        }));
        const totalRecords = response?.data?.data?.totalItems || 0;
        const totalPages = response?.data?.data?.totalPages || 0;

        setData(formatted);
        setFilteredData(formatted);
        setTotalRows(totalRecords);
        setTotalPages(totalPages);
        console.log("API Response:", response?.data);
      } else {
        console.error("API returned non-200 status:", response?.status);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData(page, pageSize, empId, empName, designation, startDate, endDate, controller.signal);
    return () => {
      controller.abort();
    };
  }, [page, pageSize]);

  useEffect(() => {
    if (!empId.trim() && !empName.trim() && !designation.trim() && !startDate && !endDate) {
      const controller = new AbortController();
      console.log("Fetching original data due to all fields cleared");
      fetchData(page, pageSize, "", "", "", null, null, controller.signal);
      return () => {
        controller.abort();
      };
    }
  }, [empId, empName, designation, startDate, endDate, page, pageSize]);

  const handlePaginationChange = paginationModel => {
    const newPage = paginationModel.page;
    const newPageSize = paginationModel.pageSize;
    setPage(newPage);
    setSize(newPageSize);
    fetchData(newPage, newPageSize, empId, empName, designation, startDate, endDate);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage, pageSize, empId, empName, designation, startDate, endDate);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      await fetchData(lastPageIndex, pageSize, empId, empName, designation, startDate, endDate);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  const handleDateChange = (date, field) => {
    const validDate = date ? dayjs(date) : null;
    if (field === "startDate") setStartDate(validDate?.isValid() ? validDate : null);
    if (field === "endDate") setEndDate(validDate?.isValid() ? validDate : null);
  };

  const handleSearch = async e => {
    if (e) e.preventDefault();
    setPage(0);
    await fetchData(0, pageSize, empId, empName, designation, startDate, endDate);
    console.log("Search triggered with:", {empId, empName, designation, startDate, endDate});
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Joining");
    XLSX.writeFile(workbook, "JoiningReport.xlsx");
  };

  const handleKeyPress = event => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const getUrl = `admin/list-designation`;
  const fetchDesignationList = async () => {
    try {
      const response = await GET(getUrl);
      const rawList = response?.data?.data;

      const formattedList = rawList.map(item => {
        let value = "";
        const designation = item.designationName.toLowerCase();
        if (designation === "manager - hr") {
          value = "manager_hr";
        } else if (designation === "operations manager") {
          value = "operations_manager";
        } else if (designation === "senior project manager") {
          value = "senior_project_manager";
        } else if (designation === "hr executive") {
          value = "hr_executive";
        } else if (designation === "qa engineer") {
          value = "qa_engineer";
        } else {
          value = designation.replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
        }
        return {
          label: item.designationName,
          value: value
        };
      });

      setDesignationList(formattedList);
    } catch (error) {
      console.error("Error fetching designation list:", error);
    }
  };

  useEffect(() => {
    fetchDesignationList();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              <form onSubmit={handleSearch}>
                <Grid container spacing={2} alignItems="centers" wrap="nowrap" >
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography
                      sx={{
                        gridColumn: "span 2",
                        color: "textPrimary",
                        fontFamily: properties.fontFamily,
                        fontSize: "15px",
                        fontWeight: "bold"
                      }}
                    >
                      Joining Report
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <MDInput
                      value={empId}
                      onChange={e => setEmpId(e.target.value)}
                      name="empId"
                      label="Employee ID"
                      variant="outlined"
                      onKeyPress={handleKeyPress}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <MDInput
                      value={empName}
                      onChange={e => setEmpName(e.target.value)}
                      name="empName"
                      label="Employee Name"
                      variant="outlined"
                      onKeyPress={handleKeyPress}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="designation-label" sx={{fontSize: "14px"}}>
                        Designation
                      </InputLabel>
                      <Select
                        labelId="designation-label"
                        id="designation"
                        value={designation}
                        label="Designation"
                        onChange={e => setDesignation(e.target.value)}
                        sx={{
                          height: "45px"
                        }}
                      >
                        <MenuItem value="">
                          <em>Select</em>
                        </MenuItem>
                        {designationList.map(item => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        label="Start Date"
                        value={startDate || null}
                        onChange={date => handleDateChange(date, "startDate")}
                        onKeyPress={handleKeyPress}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        label="End Date"
                        value={endDate || null}
                        onChange={date => handleDateChange(date, "endDate")}
                        onKeyPress={handleKeyPress}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={2}
                    display="flex"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <MDButton
                      variant="contained"
                      color="info"
                      sx={{background: properties.colors.bgcolorbluethem}}
                      type="submit"
                    >
                      Search
                    </MDButton>
                    <MDButton variant="gradient" color="success" onClick={handleDownloadExcel}>
                      Download
                    </MDButton>
                  </Grid>
                </Grid>
              </form>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredData}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[20]}
              paginationModel={{page, pageSize}}
              onPaginationModelChange={handlePaginationChange}
              loading={loading}
              getRowId={row => row.empId}
              components={{Toolbar: GridToolbar}}
              hideFooter
              sx={{
                height: "450px",
                fontWeight:400,
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important",
                },
                "& .MuiDataGrid-cell": {
                  textAlign: "left"
                }
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}